import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { MillieAvatar } from "../components/MascotBubble";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/home";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-background px-6 pt-16 pb-10 flex flex-col">
        <div className="flex flex-col items-center text-center gap-2">
          <MillieAvatar size={84} />
          <h1 className="font-logo text-5xl text-sorted-ink mt-2">sorted</h1>
          <p className="text-sorted-slate font-semibold -mt-1">Your household. Sorted.</p>
          <p className="font-hand text-2xl text-sorted-ink mt-3 max-w-xs leading-tight">Hi, I'm Millie. Tell me what's going on — I'll help sort the rest. ❤️</p>
        </div>

        <button
          onClick={googleLogin}
          data-testid="google-login"
          className="mt-8 w-full bg-white border-2 border-[#EFE2DE] text-sorted-ink font-bold py-4 rounded-full flex items-center justify-center gap-3 shadow-soft hover:bg-muted transition-colors"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-[#EFE2DE]" />
          <span className="text-xs font-bold text-sorted-slate">or</span>
          <div className="h-px flex-1 bg-[#EFE2DE]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Mail size={18} strokeWidth={2.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-sorted-slate" />
            <input data-testid="login-email" type="email" required placeholder="you@email.com" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border-2 border-[#EFE2DE] rounded-2xl pl-12 pr-4 py-4 text-sorted-ink font-medium outline-none focus:border-sorted-rose transition-colors" />
          </div>
          <div className="relative">
            <Lock size={18} strokeWidth={2.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-sorted-slate" />
            <input data-testid="login-password" type={showPw ? "text" : "password"} required placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border-2 border-[#EFE2DE] rounded-2xl pl-12 pr-12 py-4 text-sorted-ink font-medium outline-none focus:border-sorted-rose transition-colors" />
            <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="login-toggle-password" aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-sorted-slate hover:text-sorted-ink">
              {showPw ? <EyeOff size={18} strokeWidth={2.5} /> : <Eye size={18} strokeWidth={2.5} />}
            </button>
          </div>
          <div className="text-right -mt-1">
            <Link to="/forgot-password" data-testid="login-forgot" className="text-xs font-bold text-sorted-rose">Forgot password?</Link>
          </div>
          <button data-testid="login-submit" disabled={loading}
            className="w-full bg-sorted-rose hover:bg-sorted-roseHover text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-soft transition-colors disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"} {!loading && <ArrowRight size={18} strokeWidth={2.5} />}
          </button>
        </form>

        <p className="mt-auto text-center text-sorted-slate text-sm pt-8">
          New here?{" "}
          <Link to="/register" data-testid="go-register" className="font-bold text-sorted-ink underline underline-offset-2">Create an account</Link>
        </p>
        <p className="mt-2 text-center text-[11px] text-sorted-slate font-semibold pb-2" data-testid="legal-links">
          <Link to="/privacy" data-testid="login-privacy" className="underline underline-offset-2">Privacy policy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" data-testid="login-terms" className="underline underline-offset-2">Terms of service</Link>
        </p>
      </div>
    </div>
  );
}
